import { getPurchaseFunnelParameters } from "../api/purchaseFunnelApi";
import { ISeoFilters } from "../constants/interface";
import { brandPerformanceApiParamsToObject, getNissanCompetitorParam } from "./brandPerformanceApi";
import { getCpoParams } from "./cpoDashboardApi";
import { cpoOperationalParams, normalizeCpoOperationalFiltersResponse } from "./cpoOperationalApi";
import { ecommerceApiParamsToObject } from "./ecommerce";
import { getEuropeActivationParameters } from "./europeActivationParameters";
import { getParams as getExecutiveSummaryProductPerformanceParams } from "./executiveSummaryProductPerformanceApi";
import { getParams as getExecutiveSummaryDisParams } from "./executiveSummaryWebsiteApi";
import { journeyExcellenceApiParamsToObject } from "./journeyExcellenceApi";
import { midasApiParamsToObject } from "./midasApi";
import { msrpApiParamsToObject } from "./msrpApi";
import { getQueryParams as getProductPerformanceParams } from "./productPerformanceApi";
import { getParams as getTopFlopParams } from "./topFlopApi";
import { apiParamsToObjectUseOwnership } from "./useOwnershipApi";
import { apiFetchV2, fetchDataV2, newApiFetchV2, newFetchDataV2, postDataV2 } from "./utils";
import { getParams as getJourneyExcellenceMarketPerformanceSummaryParams } from "./JourneyExcellenceMarketPerformanceSummary";
import { getParams as getDmeoParams } from "./dmeoApi";
import { getParams as getCcsMonetizationParams } from "./ccsMonetizationApi";

// Distinct tokenKey (see fetchDataV2's own comment) - defense in depth now that
// SignUpForm.tsx's own redundant dispatch of the same fetch has been removed (GeographicalScopeInput.tsx
// is the sole caller), in case a future caller duplicates this fetch the same way again.
export const getGeoScopeData = () => fetchDataV2("/authentication/sign-up-geos", {}, undefined, "geo-scope-data");
export const getCpoFiltersData = () => newApiFetchV2("/certified-pre-owned/get-filters", getCpoParams());
export const getCcsCockpitFilterData = () => newFetchDataV2("/ccs/get-filter-cockpit", {});
export const getAppRatingsFilterData = () => newFetchDataV2("/ccs/get-filter-appreview-data", {});
export const getUserGroupMarkets = () => newApiFetchV2("/markets", {});
export const getMtmMarketsData = () => newApiFetchV2("/mtm_api/markets", {});
export const getSeoFilters = (): Promise<ISeoFilters> => newApiFetchV2("/seo/_filters", {});

export const getEuropeActivationFilterData = () =>
  postDataV2("/europe-activation-ratio-reporting/get-filters", getEuropeActivationParameters());

export const getEuropeActivationMarketGroups = () => fetchDataV2("/europe-activation-ratio-reporting/get-market-groups");

export const getDisFilters = () => newApiFetchV2("/dis/filters", getPurchaseFunnelParameters());

export const getTopFlopFilters = () => newApiFetchV2("/top-flop/get-filters", getTopFlopParams());

export const getProductMarketingOptimizationFilters = (brand: string) =>
  newApiFetchV2(`/product-marketing-optimization/get-filters-data?use_cache=true`, { brand: brand });

export const getMidasFilters = (params: string) => apiFetchV2("/midas/fmi-run-rate/filters?use_cache=true", midasApiParamsToObject(params));

export const getMidasFmiByModelFilters = (params: string) =>
  apiFetchV2("/midas/fmi-by-model/filters?use_cache=true", midasApiParamsToObject(params));

export const getMidasMediaMixFilters = (params: string) =>
  apiFetchV2("/midas/media-mix/filters?use_cache=true", midasApiParamsToObject(params));

export const getMidasMtpCategoryFilters = (params: string) =>
  apiFetchV2("/midas/mtp-category/filters?use_cache=true", midasApiParamsToObject(params));

export const getMidasStrategicDealerSupportFilters = (params: string) =>
  apiFetchV2("/midas/strategic-dealer-support/filters?use_cache=true", midasApiParamsToObject(params));

export const getMidasStrategicCEFilters = (params: string) =>
  apiFetchV2("/midas/strategic-ce/filters?use_cache=true", midasApiParamsToObject(params));

export const getMidasFixedFilters = (params: string) => apiFetchV2("/midas/fixed/filters?use_cache=true", midasApiParamsToObject(params));

export const getMidasSpendVsTrafficFilters = (params: string) =>
  apiFetchV2("/midas/spend-vs-traffic/filters?use_cache=true", midasApiParamsToObject(params));

export const getProductPerformanceFilters = () => apiFetchV2("/product-performance/filters", getProductPerformanceParams());

export const getUaeFilters = () => fetchDataV2("/ccs-activation-ratio/get-uae-filters?use_cache=true", {});

export const getAseanFilters = () => fetchDataV2("/ccs-activation-ratio/get-asean-filters?use_cache=true");

export const getVocFilters = () => fetchDataV2("/voc/filters");

export const getCpoOperationalFilters = (params: string) =>
  apiFetchV2("/cpo-operational/filters", cpoOperationalParams(params)).then(normalizeCpoOperationalFiltersResponse);

export const getEcommerceFilters = () => apiFetchV2("/ecom/filters", { ...ecommerceApiParamsToObject() });

export const getMsrpFilters = () => apiFetchV2("/msrp/filters", msrpApiParamsToObject());

export const getJourneyExcellenceFilters = (metric: string) => {
  let path = "/journey-excellence/fmi/filters";
  if (metric == "Online Journey") {
    path = "/journey-excellence/online-journey/filters";
  } else if (metric == "Leads Management System") {
    path = "/journey-excellence/lms/filters";
  } else if (metric == "Product Journey") {
    path = "/journey-excellence/product-journey/filters";
  }
  return apiFetchV2(path, journeyExcellenceApiParamsToObject());
};

export const getJourneyExcellenceMarketPerformanceSummaryFilters = () =>
  newApiFetchV2("/mtm/filters", getJourneyExcellenceMarketPerformanceSummaryParams());
export const getOCEModelsByBrandFilters = () => fetchDataV2("/filters/get-all-models-by-brand", {});
export const getOCEMarketsByBrandFilters = () => fetchDataV2("/filters/get-all-markets-by-brand", {});
export const getOCEChannelsByBrandFilters = () => fetchDataV2("/filters/get-all-channels-by-brand", {});

export const getOCEAllRegionsFilters = () => fetchDataV2("/filters/get-all-regions", {});
export const getOCEAllMarketRegions = () => fetchDataV2("/filters/get-all-market-regions", {});

export const getUseOwnershipFilters = (page: string) => {
  return apiFetchV2(`/use-ownership/filters?page=${page.replaceAll(" ", "%20")}`, apiParamsToObjectUseOwnership());
};

export const getBrandPerformanceVolumeFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-volume/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );
export const getBrandPerformanceOaOFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-oao/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );
export const getBrandHealthIndexFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-health-index/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBuzzTrackingFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/buzz-tracking/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );
export const getBrandPerformanceImageAttributesFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-image-attributes/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );
export const getBrandSegmentVolumeFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/segment-volume-and-segment-tiv-share-volume/filters?use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandMarketShareFilters = (params: string) =>
  apiFetchV2(`/brand-performance/brand-market-share/filters?use_cache=true`, brandPerformanceApiParamsToObject(params));
export const getMarketShareByChannelFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/market-share-by-channel/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandKPITrendFilters = (params: string) =>
  apiFetchV2(`/brand-performance/brand-kpi-trend/filters?use_cache=true`, brandPerformanceApiParamsToObject(params));
export const getBrandPerformanceSovSomFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/sov-som/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandKPISummaryFilters = (params: string) =>
  apiFetchV2(`/brand-performance/brand-kpi-summary/filters?use_cache=true`, brandPerformanceApiParamsToObject(params));

export const getPurchaseFunnelFilters = (params: string) =>
  apiFetchV2(`/brand-performance/purchase-funnel/filters?use_cache=true`, brandPerformanceApiParamsToObject(params));

// The real backend (NS-968, cedar-backend's ns-968-eur-epwr-awareness branch) named this
// "eur-epwr-awareness", not "tech-awareness" - this was a placeholder URL (see git history) built
// before that backend existed. EurEpwrAwarenessFilters' own allowed_fields (schemas/buy/
// brand_performance.py) is just date_range/start_date/end_date/brand/market/granularity - every
// other field this page's own filter tile sends (competitor/category/model/...) is silently
// dropped server-side (remove_unused_fields_from_filter), not rejected, so no request-shape
// change is needed here despite those extra controls having no effect on the real response.
export const getTechAwarenessFilters = (params: string) =>
  apiFetchV2(`/brand-performance/eur-epwr-awareness/filters?use_cache=true`, brandPerformanceApiParamsToObject(params));

export const getNIMFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/nim/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );
export const getImageSnapshotFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-image-snapshot/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );

export const getNIMLatestFilters = (params: string) => {
  return apiFetchV2(
    `/brand-performance/nim-latest/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );
};

export const getRVFilters = (params: string) =>
  apiFetchV2(`/brand-performance/residaul-value/filters?use_cache=true`, brandPerformanceApiParamsToObject(params));

export const getMonthlyTableForDownloadFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/monthly-table-for-download/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );

export const getYearlyTableForDownloadFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/yearly-table-for-download/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );

export const getQuarterlyTableForDownloadFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/quarterly-table-for-download/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );

export const getRVTableForDownloadFilters = (params: string) =>
  apiFetchV2(
    `/brand-performance/rv-table-for-download/filters?nissan_competitor=${getNissanCompetitorParam(params)}&use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandPerformanceTPVAFilters = (params: string) =>
  apiFetchV2(`/brand-performance/tpva/filters?use_cache=true`, brandPerformanceApiParamsToObject(params));

export const getBrandPerformanceTPFilters = (params: string) =>
  apiFetchV2(`/brand-performance/tp/filters?use_cache=true`, brandPerformanceApiParamsToObject(params));

export const getExecutiveSummaryDisFilters = () => apiFetchV2("/executive-summary/dis/filters", getExecutiveSummaryDisParams());
export const getExecutiveSummaryProductPerformanceFilters = () =>
  apiFetchV2("/executive-summary/product-performance/filters", getExecutiveSummaryProductPerformanceParams());
export const getExecutiveSummaryProductPerformanceDeepDiveFilters = () =>
  apiFetchV2("/executive-summary/product-performance-deep-dive/filters", getExecutiveSummaryProductPerformanceParams());

export const getDmeoFilters = () => newApiFetchV2("/dmeo/filters", getDmeoParams());

export const getCcsMonetizationFilters = () => newApiFetchV2("/ccs_monetization/filters", getCcsMonetizationParams());
